import { Card, CardProps, Stack, StackProps, Typography } from '@mui/material';
import { mixinSx } from '@watershed/style/styleUtils';
import { forwardRef, ReactNode } from 'react';

function Well({ sx, ...props }: StackProps) {
  return (
    <Stack
      component="section"
      {...props}
      className="UIWell"
      sx={mixinSx(
        {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.grey10,
          borderRadius: '10px',
          padding: '4px',
        },
        sx
      )}
    />
  );
}

export default Well;

Well.Header = function WellHeader({
  title,
  subtitle,
  titleLevel = 2,
  titleVariant = 'h3',
  subtitleVariant = 'body2',
  titleColor = 'secondary',
  actions,
  sx,
}: Pick<StackProps, 'sx'> & {
  title: ReactNode;
  subtitle?: ReactNode;
  titleLevel?: 2 | 3 | 4;
  subtitleLevel?: 2 | 3 | 4;
  titleVariant?: 'h2' | 'h3' | 'h4' | 'h5';
  subtitleVariant?: 'body2' | 'body3';
  titleColor?: 'primary' | 'secondary';
  actions?: ReactNode;
}) {
  const component = `h${titleLevel}` as keyof JSX.IntrinsicElements;
  return (
    <Stack
      component="header"
      direction="row"
      justifyContent="space-between"
      className="UIWellHeader"
      sx={mixinSx(
        {
          gap: 1.5,
          padding: 1.5,
          alignItems: 'center',
          position: 'relative',
        },
        sx
      )}
    >
      <Stack>
        <Typography
          variant={titleVariant}
          component={component}
          sx={{
            color: (theme) =>
              titleColor === 'primary'
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            textWrap: 'balance',
          }}
          children={title}
        />
        {subtitle && (
          <Typography
            variant={subtitleVariant}
            sx={{ textWrap: 'balance' }}
            children={subtitle}
          />
        )}
      </Stack>
      {actions && (
        <Stack direction="row" gap={1} alignItems="center">
          {actions}
        </Stack>
      )}
    </Stack>
  );
};

export type WellContentProps = Omit<
  CardProps,
  | 'className'
  // We're not using the `tw` experimental Tailwind prop, which is marked as
  // optional in `MuiButtonProps`. But for some reason, when we introduced
  // `next-env.d.ts` to `ui-core`, it started causing this bizarre type error,
  // so we'll just omit it here.
  // More details:
  // https://github.com/watershed-climate/ghg/pull/40050#issuecomment-1673322341
  | 'tw'
>;

Well.Content = forwardRef<HTMLDivElement, WellContentProps>(
  function WellContent({ children, sx, ...props }: WellContentProps, ref) {
    return (
      <Card
        component="article"
        {...props}
        ref={ref}
        className="UIWellContent"
        sx={mixinSx(
          {
            padding: 1.5,
            borderRadius: 1,
            position: 'relative',
          },
          sx
        )}
      >
        {children}
      </Card>
    );
  }
);

Well.Footer = forwardRef<HTMLDivElement, StackProps>(function WellFooter(
  { children, sx, ...props }: StackProps,
  ref
) {
  return (
    <Stack
      component="footer"
      direction="row"
      justifyContent="flex-end"
      className="UIWellFooter"
      sx={mixinSx(
        {
          pt: '4px',
        },
        sx
      )}
      {...props}
      ref={ref}
    >
      {children}
    </Stack>
  );
});
